// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { sharedTagStyle } from "@bg/XDL/Utilities/sharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateOnlyStringToDate } from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderApprovalList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { DatePickerState } from "@insite/mobius/DatePicker";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    filtersOpen: state.pages.orderApprovalList.filtersOpen,
    getOrderApprovalsParameter: state.pages.orderApprovalList.getOrderApprovalsParameter,
    language: getSession(state).language,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderApprovalListTagsStyles {
    appliedFiltersContainer?: InjectableCss;
}

export const orderApprovalListFilterStyles: OrderApprovalListTagsStyles = {
    appliedFiltersContainer: {
        css: css`
            ${horizontalStyles}
            && div[class*=""TagStyle-sc] {
                width: auto;
                height: auto !important;
            }
        `,
    },
};

export const styles = orderApprovalListFilterStyles;

let updateTimeoutId: NodeJS.Timeout | undefined;

const orderApprovalListFilterTags = ({
    language,
    getOrderApprovalsParameter,
    filtersOpen,
    updateSearchFields,
}: Props) => {
    if (filtersOpen) {
        return null;
    }
    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({ toDate: "" });
    };

    const shipToRemoveHandler = () => {
        updateSearchFields({ shipToId: "" });
    };
    const orderNumberChangeHandler = () => {
        updateTimeoutId = setTimeout(() => {
            updateSearchFields({
                orderNumber: "",
            });
        }, 250);
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({ fromDate: "" });
    };

    const orderTotalChangeHandler = () => {
        updateTimeoutId = setTimeout(() => {
            updateSearchFields({
                orderTotalOperator: "",
                orderTotal: "",
            });
        }, 250);
    };

    return (
        <StyledWrapper {...styles.appliedFiltersContainer}>
            {getOrderApprovalsParameter.shipToId && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        shipToRemoveHandler();
                    }}
                >
                    {`${translate("Ship to Address")}: ${getOrderApprovalsParameter.shipToId}`}
                </Tag>
            )}
            {getOrderApprovalsParameter.orderNumber && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        orderNumberChangeHandler();
                    }}
                >
                    {`${translate("Order #")}: ${getOrderApprovalsParameter.orderNumber}`}
                </Tag>
            )}
            {getOrderApprovalsParameter.fromDate && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        fromDateChangeHandler({} as any);
                    }}
                >
                    {`${translate("From")}: ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(getOrderApprovalsParameter.fromDate)!,
                        language,
                    })}`}
                </Tag>
            )}
            {getOrderApprovalsParameter.toDate && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        toDateChangeHandler({} as any);
                    }}
                >
                    {`${translate("To")}: ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(getOrderApprovalsParameter.toDate)!,
                        language,
                    })}`}
                </Tag>
            )}
            {getOrderApprovalsParameter.orderTotalOperator && getOrderApprovalsParameter.orderTotal && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        orderTotalChangeHandler();
                    }}
                >
                    {`${translate("Order Total")}: ${getOrderApprovalsParameter.orderTotalOperator} ${
                        getOrderApprovalsParameter.orderTotal
                    }`}
                </Tag>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(orderApprovalListFilterTags),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Tags",
        allowedContexts: ["OrderApprovalListPage"],
        isSystem: true,
    },
};

export default widgetModule;
